import { P2GBox, P2GCheckBoxLegacy, P2GGrid, P2GText, P2GProgress, P2GChip } from '@parcel2go/core-ui';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ExtraBase, RuleApiModel, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ExtrasDescription, ExtrasIcon, ExtrasIconSrc, ExtrasTitle, ThirdPartyEmail } from '../../ExtrasHelper';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { GetDefaultRuleByName } from '../rules/RuleLocator';
import {
  generateDefaultExtrasRule,
  generateDefaultExtrasRuleActions,
  GetExtrasFromRule,
  RuleNames
} from '../rules/RuleGenerator';
import { EventSections } from '../../../lib/events/EventLogger';
import { RoyalMailProvider, SmartSendProvider } from '~/lib/consts/providers';
import { PanelButtons } from '../panels/PanelButtons';
import { onlyUnique } from '../../../lib/helpers/ArrayHelper';
import { ExtraAlwaysIncluded } from '../../../lib/helpers/ExtraAlwaysIncluded';
import { GetExtrasFromSettings } from '~/lib/settings/settings-helper';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { ToExtraTitleTranslationKey } from '~/apps/smartsend/lib/helpers/ToUniqueExtras';
import { ToExtraDescriptionTranslationKey } from '~/apps/smartsend/lib/helpers/ToUniqueExtras';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props {
  confirm?: (extras: ExtraBase[]) => void;
  cancel?: () => void;
  affected: number;
  selectedItemCount?: number;
  filterContext?: string;
  include: string[];
  exclude: string[];
  showDefaultRule: boolean;
  getSelectedItems: () => SmartSendItemApiModel[];
  getRules: (type: 'SmartSend') => Promise<RuleApiModel[]>;
  createRule: (rule: RuleApiModel) => Promise<boolean>;
  updateRule: (rule: RuleApiModel) => Promise<boolean>;
}

interface extraProps {
  extra: ExtraBase;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  price?: ReactNode;
  children?: ReactNode;
}

interface extraOption {
  extra: ExtraBase;
  availableFor: number;
}

export const ExtraRow: React.FC<extraProps> = (props) => {
  const onClick = props.disabled ? () => {} : props.onClick;
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  return (
    <P2GBox onClick={onClick}>
      <P2GGrid container gap={2} wrap="nowrap" alignItems="center">
        <P2GGrid item flexShrink={1}>
          <CdnIconProxy size="sm" palette="primary" src={ExtrasIconSrc(props.extra) || ''}>
            <ExtrasIcon Id={props.extra.Id} />
          </CdnIconProxy>
        </P2GGrid>
        <P2GGrid item flexGrow={1}>
          <P2GText>
            <strong>{t(ToExtraTitleTranslationKey(props.extra), { defaultValue: ExtrasTitle(props.extra) })}</strong>
          </P2GText>
          <P2GText component="p" variant="body2">
            <Trans
              t={t}
              i18nKey={ToExtraDescriptionTranslationKey(props.extra)}
              values={{ description: ExtrasDescription(props.extra) }}
              components={{
                price: <>{props.price}</>
              }}
              defaults="{{description}} <price />"
            />
          </P2GText>
          {props.children && <P2GBox mt={1}>{props.children}</P2GBox>}
        </P2GGrid>
        <P2GGrid item flexShrink={1}>
          <P2GCheckBoxLegacy
            id={props.extra.Id}
            checked={props.selected}
            onChange={() => {}}
            disabled={props.disabled}
            labelPlacement="start"
            variant="primary"
            error={false}
          />
        </P2GGrid>
      </P2GGrid>
    </P2GBox>
  );
};

export default function Extras(props: props) {
  const {
    affected,
    cancel,
    confirm: confirmSelection,
    createRule,
    exclude,
    getRules,
    getSelectedItems,
    include,
    showDefaultRule,
    updateRule
  } = props;
  const [loading, setLoading] = useState(props.showDefaultRule);
  const [extras, setExtras] = useState<ExtraBase[]>([]);
  const [rule, setRule] = useState<RuleApiModel>();
  const [save, setSave] = useState(false);
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const options = useMemo<extraOption[]>(() => {
    const selectedItems = getSelectedItems();

    const getItemCountWhichCanHaveThisExtra = (extra: string) => {
      return selectedItems.filter(
        (x) => !!x.State?.validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras?.find((a) => a.Id === extra)
      ).length;
    };

    const availableExtras = selectedItems
      .filter((x) => !!x.State?.validation?.QuoteState?.Quote?.Options[0])
      .flatMap((x) =>
        x.State.validation.QuoteState.Quote.Options[0].AvailableExtras.filter(
          (a) => !ExtraAlwaysIncluded(a.Id, x.State.validation.QuoteState.Quote.Service)
        ).map((a) => a.Id)
      )
      .filter(onlyUnique);

    const baseExtras = GetExtrasFromSettings(SettingsCache.settings);

    // TODO Add via config from Edge API.
    // Needs UI deploying first, then Edge API config, then we can remove this.
    if (
      selectedItems.find((x) => x.Service?.Provider === RoyalMailProvider) &&
      getItemCountWhichCanHaveThisExtra(ThirdPartyEmail) > 0
    ) {
      baseExtras.push({
        Id: ThirdPartyEmail,
        Provider: SmartSendProvider,
        Name: 'Third Party Email'
      });
    }

    return baseExtras
      .filter((x) => include.length == 0 || include.includes(x.Id))
      .filter((x) => exclude.length == 0 || !exclude.includes(x.Id))
      .filter((x) => availableExtras.includes(x.Id))
      .map((x) => {
        return {
          extra: x,
          availableFor: getItemCountWhichCanHaveThisExtra(x.Id)
        };
      });
  }, [exclude, include]);

  // RuleManager Rules
  useEffect(() => {
    if (showDefaultRule) {
      getRules('SmartSend')
        .then((r) => {
          const dRule = r.find((x) => GetDefaultRuleByName(x, RuleNames.Extras));
          if (dRule) {
            setRule(dRule);
            setSave(true);
            setExtras(GetExtrasFromRule(dRule));
          }
        })
        .finally(() => setLoading(false));
    }
  }, [getRules, showDefaultRule]);

  const toggle = (selection: ExtraBase) => {
    let newExtras = [...extras];
    if (newExtras.find((e) => e.Id === selection.Id)) {
      newExtras = newExtras.filter((x) => x.Id !== selection.Id);
    } else {
      newExtras.push(selection);
    }
    setExtras(newExtras);
  };

  const confirm = () => {
    const toAdd = [...extras];

    // RuleManager
    if (showDefaultRule && save) {
      if (rule) {
        updateRule({ ...rule, Actions: generateDefaultExtrasRuleActions(toAdd) });
      } else {
        track({
          type: 'inline_rules_added',
          payload: GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.InlineRules], {
            rule: 'default-extras'
          })
        });
        createRule(generateDefaultExtrasRule(toAdd));
      }
    }

    confirmSelection(toAdd);
  };

  return (
    <P2GBox textAlign="left">
      {loading ? (
        <P2GBox>
          <P2GProgress />
        </P2GBox>
      ) : (
        <P2GBox>
          {options.length == 0 ? (
            <>
              <P2GText>
                {t('NO_ITEMS_TO_ADD_EXTRAS_ERROR', { defaultValue: 'There are no items which you can add extras to.' })}
              </P2GText>
            </>
          ) : (
            <P2GGrid container direction="column" spacing={3}>
              {options.map((e) => (
                <P2GGrid item key={e.extra.Id}>
                  <ExtraRow
                    extra={e.extra}
                    onClick={() => toggle(e.extra)}
                    selected={!!extras.find((x) => x.Id === e.extra.Id)}
                  >
                    {affected > e.availableFor && (
                      <P2GChip
                        label={
                          e.availableFor == 1
                            ? t('ONLY_AVAILABLE_ON_ONE_ITEM', { defaultValue: 'Only available on 1 item' })
                            : t('ONLY_AVAILABLE_ON_MULTIPLE_ITEMS', {
                                defaultValue: 'Only available on {{count}} items',
                                count: e.availableFor
                              })
                        }
                        size="small"
                        color="info"
                      />
                    )}
                  </ExtraRow>
                </P2GGrid>
              ))}
              {showDefaultRule && (
                <P2GGrid item>
                  <P2GCheckBoxLegacy
                    checked={save}
                    label={
                      <P2GText variant="body2">
                        {t('AUTO_APPLY_INPUT_LABEL', { defaultValue: 'Auto apply when importing new items' })}
                      </P2GText>
                    }
                    id="Save"
                    onChange={setSave}
                    labelPlacement="end"
                    variant="primary"
                    error={false}
                  />
                </P2GGrid>
              )}
            </P2GGrid>
          )}
        </P2GBox>
      )}
      <P2GBox mt={2} />
      <PanelButtons
        onSave={confirmSelection && confirm}
        saveDisabled={loading}
        onCancel={cancel}
        cancelText={t('CANCEL_BUTTON', { defaultValue: 'Cancel' })}
      />
    </P2GBox>
  );
}
